import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { ApiResponse } from '../../../../../shared/dtos/api-response';
import { ApiError } from '../../../../../shared/dtos/api-error';
import { TerminationRequestMapper } from '../../models/termination';
import type { TerminationRequest } from '../../models/termination';
import { TerminationsStore } from '../../stores/terminations.store';
import {
  INTERNAL_SUPPLIER_TERMINATION_APPROVE_ENDPOINT,
  INTERNAL_SUPPLIER_TERMINATION_CREATE_ENDPOINT,
  INTERNAL_SUPPLIER_TERMINATION_REJECT_ENDPOINT,
  INTERNAL_SUPPLIER_TERMINATIONS_ENDPOINT,
} from '../../constants/endpoints';
import type { TerminationRequestSummary } from '../../dtos/termination';
import { startAuthenticatedPolling } from '../../../../../shared/utils/authenticated-polling';

@Injectable({ providedIn: 'root' })
export class TerminationService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(TerminationsStore);

  constructor() {
    startAuthenticatedPolling(() => this.getTerminations());
  }

  getTerminations(): Observable<TerminationRequest[]> {
    this.store.setLoading();
    return this.http
      .get<ApiResponse<TerminationRequestSummary[]>>(INTERNAL_SUPPLIER_TERMINATIONS_ENDPOINT)
      .pipe(
        map((resp) => TerminationRequestMapper.fromDtoList(resp.data)),
        tap((records) => this.store.setRecords(records)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          this.store.setError(body?.message ?? 'Failed to load terminations.');
          return throwError(() => body ?? { message: 'Failed to load terminations.' });
        }),
      );
  }

  createTermination(distributorId: number | string): Observable<TerminationRequest> {
    return this.http
      .post<
        ApiResponse<TerminationRequestSummary>
      >(INTERNAL_SUPPLIER_TERMINATION_CREATE_ENDPOINT('' + distributorId), {})
      .pipe(
        map((resp) => TerminationRequestMapper.fromDto(resp.data)),
        tap((record) => this.store.addRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to create termination request.' });
        }),
      );
  }

  approveTermination(terminationId: number): Observable<TerminationRequest> {
    return this.http
      .put<
        ApiResponse<TerminationRequestSummary>
      >(INTERNAL_SUPPLIER_TERMINATION_APPROVE_ENDPOINT(terminationId), {})
      .pipe(
        map((resp) => TerminationRequestMapper.fromDto(resp.data)),
        tap((record) => this.store.updateRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to approve termination.' });
        }),
      );
  }

  rejectTermination(terminationId: number): Observable<TerminationRequest> {
    return this.http
      .delete<
        ApiResponse<TerminationRequestSummary>
      >(INTERNAL_SUPPLIER_TERMINATION_REJECT_ENDPOINT(terminationId))
      .pipe(
        map((resp) => TerminationRequestMapper.fromDto(resp.data)),
        tap((record) => this.store.updateRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to reject termination.' });
        }),
      );
  }
}
